/* Catálogo de capabilities + matriz plano→capability (ADR-0320).

   Fonte ÚNICA da matriz (§4.17): tanto o `config-service` (servir/editar — admin
   no Epic 28) quanto o `licensing-service` (derivar o claim do JWT) importam daqui.
   Dep-light (só zod) → seguro para Cloudflare Workers. */
import { z } from 'zod';

/* Catálogo de capability ids v1 — reservar já. Id fora daqui (ou ausente da
   matriz do plano) = NÃO concedido (default deny / fail-closed). Versionável:
   adicionar o id aqui + nos planos que o concedem na matriz. */
export const CAPABILITY_IDS = [
  'cloud.sync',
  'ai.tokens',
  'history.retention',
  'seats',
  'packs.private',
  'collab.realtime',
] as const;
export type CapabilityId = (typeof CAPABILITY_IDS)[number];

/* Capabilities RESERVADAS: id versionado no catálogo mas concedido em NENHUM plano
   (implementação futura). `collab.realtime` = Epic 36; `cloud.sync` = serviço de sync
   futuro (ADR-0237 §2, ADR-0606). Reservar ≠ remover: religar a um plano é só editar a
   matriz. O gate `check:capability-consumer` isenta reservadas do requisito de enforcement
   E valida a coerência (reservada NÃO pode ser truthy em plano algum). */
export const RESERVED_CAPABILITY_IDS = ['collab.realtime', 'cloud.sync'] as const;
export type ReservedCapabilityId = (typeof RESERVED_CAPABILITY_IDS)[number];

/** `true` se o id é reservado (sem enforcement esperado; false em todos os planos). */
export function isReservedCapability(id: string): boolean {
  return (RESERVED_CAPABILITY_IDS as readonly string[]).includes(id);
}

/** Valor de uma capability: boolean (liga/desliga) ou número (quota). */
export const capabilityValueSchema = z.union([z.boolean(), z.number()]);
export type CapabilityValue = z.infer<typeof capabilityValueSchema>;

/** Matriz `plano → (capabilityId → valor)`. Capability ausente = não concedida. */
export const capabilityMatrixSchema = z.record(
  z.string(),
  z.record(z.string(), capabilityValueSchema),
);
export type CapabilityMatrix = z.infer<typeof capabilityMatrixSchema>;

/* Seed v1 (PLANNING §14.8). As capabilities de `RESERVED_CAPABILITY_IDS`
   (`collab.realtime` = Epic 36; `cloud.sync` = serviço de sync futuro, ADR-0606) ficam
   `false` em TODO plano — religá-las a um plano é só editar a matriz. Quotas
   (`ai.tokens`/`seats`/`history.retention`) alinhadas ao restante de DEFAULT_CONFIG. */
export const DEFAULT_CAPABILITY_MATRIX: CapabilityMatrix = {
  free: {
    'cloud.sync': false,
    'ai.tokens': 0,
    'history.retention': 7,
    seats: 1,
    'packs.private': false,
    'collab.realtime': false,
  },
  pro: {
    'cloud.sync': false,
    'ai.tokens': 50_000,
    'history.retention': 30,
    seats: 1,
    'packs.private': false,
    'collab.realtime': false,
  },
  team: {
    'cloud.sync': false,
    'ai.tokens': 200_000,
    'history.retention': 90,
    seats: 10,
    'packs.private': true,
    'collab.realtime': false,
  },
  enterprise: {
    'cloud.sync': false,
    'ai.tokens': 1_000_000,
    'history.retention': 365,
    seats: 9999,
    'packs.private': true,
    'collab.realtime': false,
  },
};

/**
 * Deriva as capabilities efetivas de um usuário (ADR-0320): união das do plano
 * com as de add-ons comprados. v1 deriva só do plano (add-ons = fase 2, liga
 * 34↔35); add-ons sobrescrevem o plano quando informados. Plano desconhecido →
 * `{}` (fail-closed).
 */
export function deriveCapabilities(
  plan: string,
  matrix: CapabilityMatrix,
  addOns: Readonly<Record<string, CapabilityValue>> = {},
): Record<string, CapabilityValue> {
  return { ...(matrix[plan] ?? {}), ...addOns };
}

/** `true` se a capability está presente e truthy (boolean true ou número > 0). */
export function capabilityGranted(
  capabilities: Readonly<Record<string, CapabilityValue>>,
  id: string,
): boolean {
  const value = capabilities[id];
  if (typeof value === 'number') return value > 0;
  return value === true;
}

/* Subset de capabilities HERDÁVEIS entre planos (ADR-0595): SOMENTE as que gateiam CONTEÚDO do
   marketplace (packages/templates). Um plano DIRECIONADO pode herdar estas de outro plano (ex.: a
   org X herda o acesso do `pro` aos templates/packages), SEM herdar as PRÓPRIAS do plano-alvo
   (ai.tokens/seats/history.retention/cloud.sync = quotas/features do plano, não conteúdo). */
export const PACKAGE_TEMPLATE_CAPABILITY_IDS = ['packs.private'] as const;
export type PackageTemplateCapabilityId = (typeof PACKAGE_TEMPLATE_CAPABILITY_IDS)[number];

/** `true` se a capability gateia conteúdo de marketplace (packages/templates) → é herdável. */
export function isInheritableCapability(id: string): boolean {
  return (PACKAGE_TEMPLATE_CAPABILITY_IDS as readonly string[]).includes(id);
}

/** União de dois valores de capability na herança: concede se QUALQUER lado concede; número = máximo. */
export function unionCapabilityValue(
  a: CapabilityValue | undefined,
  b: CapabilityValue,
): CapabilityValue {
  if (a === undefined) return b;
  if (typeof a === 'number' && typeof b === 'number') return Math.max(a, b);
  return capabilityGranted({ v: a }, 'v') || capabilityGranted({ v: b }, 'v');
}
